// D7-06 (FE-6.5) + D5-14 (FE-2.6): shared pull-to-refresh gesture hook.
// Extracted from Sidebar's inline implementation so the chat message list
// (and any future scrollable surface) gets the identical touch behavior
// without re-duplicating the gesture math.
import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';

export interface UsePullToRefreshOptions {
  /** The scrollable container the gesture is attached to. */
  scrollRef: React.RefObject<HTMLElement | null>;
  /** Invoked when the user releases past the threshold. */
  onRefresh: () => Promise<unknown> | unknown;
  /** Gate the gesture (e.g. only when a list view is showing). Default true. */
  enabled?: boolean;
  /** Pull distance (px) required to trigger a refresh on release. */
  threshold?: number;
  /** Maximum rendered pull distance (px). */
  maxPullDistance?: number;
  /** Resistance multiplier applied to raw finger travel. */
  resistance?: number;
}

export interface PullToRefreshTouchHandlers {
  onTouchStart: (event: React.TouchEvent<HTMLElement>) => void;
  onTouchMove: (event: React.TouchEvent<HTMLElement>) => void;
  onTouchEnd: () => void;
}

export interface UsePullToRefreshResult {
  /** True on touch-capable devices; the gesture is inert elsewhere. */
  isTouchDevice: boolean;
  /** Current rendered pull distance in px (0 when idle). */
  pullDistance: number;
  /** True while the `onRefresh` promise is in flight. */
  isRefreshing: boolean;
  /** True once the pull has crossed the release threshold. */
  willRefresh: boolean;
  /** Spread onto the scroll container: `{...touchHandlers}`. */
  touchHandlers: PullToRefreshTouchHandlers;
}

export const PULL_TO_REFRESH_THRESHOLD = 56;
export const PULL_TO_REFRESH_MAX_DISTANCE = 88;
export const PULL_TO_REFRESH_RESISTANCE = 0.45;

export function usePullToRefresh({
  scrollRef,
  onRefresh,
  enabled = true,
  threshold = PULL_TO_REFRESH_THRESHOLD,
  maxPullDistance = PULL_TO_REFRESH_MAX_DISTANCE,
  resistance = PULL_TO_REFRESH_RESISTANCE,
}: UsePullToRefreshOptions): UsePullToRefreshResult {
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartYRef = useRef<number | null>(null);
  const canPullRef = useRef(false);

  useEffect(() => {
    setIsTouchDevice(typeof window !== 'undefined' && 'ontouchstart' in window);
  }, []);

  const onTouchStart = useCallback((event: React.TouchEvent<HTMLElement>) => {
    if (!isTouchDevice || !enabled || isRefreshing) {
      return;
    }

    canPullRef.current = (scrollRef.current?.scrollTop || 0) <= 0;
    touchStartYRef.current = event.touches[0]?.clientY ?? null;
  }, [enabled, isRefreshing, isTouchDevice, scrollRef]);

  const onTouchMove = useCallback((event: React.TouchEvent<HTMLElement>) => {
    if (!canPullRef.current || touchStartYRef.current === null || !enabled || isRefreshing) {
      return;
    }

    const nextDistance = (event.touches[0]?.clientY ?? 0) - touchStartYRef.current;
    if (nextDistance <= 0) {
      setPullDistance(0);
      return;
    }

    event.preventDefault();
    setPullDistance(Math.min(maxPullDistance, nextDistance * resistance));
  }, [enabled, isRefreshing, maxPullDistance, resistance]);

  const onTouchEnd = useCallback(() => {
    const shouldRefresh = enabled && pullDistance >= threshold && !isRefreshing;
    touchStartYRef.current = null;
    canPullRef.current = false;
    setPullDistance(0);

    if (!shouldRefresh) {
      return;
    }

    setIsRefreshing(true);
    void Promise.resolve()
      .then(() => onRefresh())
      .finally(() => {
        setIsRefreshing(false);
      });
  }, [enabled, isRefreshing, onRefresh, pullDistance, threshold]);

  return {
    isTouchDevice,
    pullDistance,
    isRefreshing,
    willRefresh: pullDistance >= threshold,
    touchHandlers: { onTouchStart, onTouchMove, onTouchEnd },
  };
}

export default usePullToRefresh;
